import { useLocation, useNavigate } from 'react-router-dom';
import { TRIGRAM_MAP, getTrigramFromSums, getHexagram } from '@/mocks/hexagrams';
import type { HexagramData } from '@/mocks/hexagrams';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { callDeepseek } from '@/lib/deepseek';
import MarkdownContent from '@/components/MarkdownContent';
import { updateInterpretation } from '@/utils/divinationStorage';

interface RoundRecord {
  round: number;
  results: string[];
  coinValues: number[];
  total: number;
}

/** 根据变爻数量生成解卦规则文本 */
function getChangingLinesRule(
  changing: Array<{ round: number; type: string }>,
  hexagram: HexagramData,
  changedHexagram: HexagramData | null,
): string {
  const count = changing.length;
  const lineNums = changing.map((l) => l.round).sort((a, b) => a - b);
  const lineStrs = lineNums.map((n) => `第${n}爻`).join('、');

  if (count === 0) {
    return `\n\n【解卦规则——静卦】\n六爻皆不变，为「静卦」。请以本卦「${hexagram.name}」的卦辞为根本依据进行解读。卦辞原文：「${hexagram.judgment}」\n六爻皆安稳，事态平稳，按卦辞所示即可。`;
  }

  if (count === 1) {
    const ln = lineNums[0];
    const yaoIndex = ln - 1;
    const yaoCi = hexagram.lines[yaoIndex] || '';
    return `\n\n【解卦规则——一爻变】\n第${ln}爻为变爻。一爻变则以本卦变爻的爻辞为主进行解读。\n本卦第${ln}爻辞：「${yaoCi}」\n请重点解析此爻辞的含义，并结合所问之事给出具体指引。`;
  }

  if (count === 2) {
    const lower = lineNums[0];
    const upper = lineNums[1];
    const yaoUpper = hexagram.lines[upper - 1] || '';
    const yaoLower = hexagram.lines[lower - 1] || '';
    return `\n\n【解卦规则——二爻变】\n第${lower}爻、第${upper}爻为变爻。二爻变则以两个变爻中上爻（第${upper}爻）的爻辞为主进行解读，下爻（第${lower}爻）作参考。\n上爻（主）第${upper}爻辞：「${yaoUpper}」\n下爻（参）第${lower}爻辞：「${yaoLower}」\n请以上爻辞为核心展开解读，下爻辞为辅佐参考。`;
  }

  if (count === 3) {
    const changedName = changedHexagram?.name || '';
    return `\n\n【解卦规则——三爻变】\n${lineStrs}为变爻。三爻变则以本卦「${hexagram.name}」卦辞为主、变卦「${changedName}」卦辞为辅，综合进行预测。\n本卦辞：「${hexagram.judgment}」\n变卦辞：「${changedHexagram?.judgment || ''}」\n请结合两卦卦辞，给出从当下到未来的完整推演。`;
  }

  if (count === 4) {
    const allLines = [1, 2, 3, 4, 5, 6];
    const unchanging = allLines.filter((n) => !lineNums.includes(n)).sort((a, b) => a - b);
    const lowerUnchanging = unchanging[0];
    const upperUnchanging = unchanging[1];
    const yaoLower = hexagram.lines[lowerUnchanging - 1] || '';
    const yaoUpper = hexagram.lines[upperUnchanging - 1] || '';
    return `\n\n【解卦规则——四爻变】\n${lineStrs}为变爻。四爻变则以两个不变爻中下爻（第${lowerUnchanging}爻）的爻辞为主进行解读，上爻（第${upperUnchanging}爻）作参考。\n下爻（主）第${lowerUnchanging}爻辞：「${yaoLower}」\n上爻（参）第${upperUnchanging}爻辞：「${yaoUpper}」\n请以下爻辞为核心展开。`;
  }

  if (count === 5) {
    const allLines = [1, 2, 3, 4, 5, 6];
    const unchanging = allLines.filter((n) => !lineNums.includes(n));
    const ln = unchanging[0];
    const yaoCi = hexagram.lines[ln - 1] || '';
    return `\n\n【解卦规则——五爻变】\n${lineStrs}为变爻。五爻变则以唯一不变爻（第${ln}爻）的爻辞为主进行解读。\n不变爻第${ln}爻辞：「${yaoCi}」\n请围绕此爻辞深入解析。`;
  }

  // count === 6
  const changedName = changedHexagram?.name || '';
  if (changedName === '乾为天') {
    return `\n\n【解卦规则——六爻皆变（乾卦用九）】\n六爻全变。本卦为「${hexagram.name}」，变卦为「乾为天」。六爻皆变的乾卦，请以「用九：见群龙无首，吉」来进行解读。`;
  }
  if (changedName === '坤为地') {
    return `\n\n【解卦规则——六爻皆变（坤卦用六）】\n六爻全变。本卦为「${hexagram.name}」，变卦为「坤为地」。六爻皆变的坤卦，请以「用六：利永贞」来进行解读。`;
  }
  return `\n\n【解卦规则——六爻皆变】\n六爻全变。请以变卦「${changedName}」的卦辞进行解读。\n变卦辞：「${changedHexagram?.judgment || ''}」`;
}

export default function Interpretation() {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as {
    question?: string;
    rounds?: RoundRecord[];
    lowerTrigram?: string;
    upperTrigram?: string;
    hexagram?: HexagramData;
    interpretation?: string;
    recordId?: string;
  } | null;

  const question = state?.question || '';
  const rounds = state?.rounds || [];
  const lowerTrigram = state?.lowerTrigram || '';
  const upperTrigram = state?.upperTrigram || '';
  const hexagram = state?.hexagram;
  const savedInterpretation = state?.interpretation || '';
  const recordId = state?.recordId || '';

  const lowerInfo = TRIGRAM_MAP[lowerTrigram];
  const upperInfo = TRIGRAM_MAP[upperTrigram];

  const [aiInterpretation, setAiInterpretation] = useState(savedInterpretation);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [useFallback, setUseFallback] = useState(false);

  // rounds[0]=初爻(最下), rounds[5]=上爻(最上)
  const yinYangLines = useMemo(() => {
    return rounds.map((r) => {
      let lineType = '';
      let changing = false;
      if (r.total === 9) { lineType = '老阳'; changing = true; }
      else if (r.total === 7) { lineType = '少阳'; changing = false; }
      else if (r.total === 8) { lineType = '少阴'; changing = false; }
      else if (r.total === 6) { lineType = '老阴'; changing = true; }
      else { lineType = r.total % 2 === 1 ? '少阳' : '少阴'; }
      return {
        round: r.round,
        total: r.total,
        type: (r.total === 9 || r.total === 7) ? '阳' : '阴',
        lineType,
        changing,
      };
    });
  }, [rounds]);

  const changingLines = yinYangLines.filter((l) => l.changing);
  const hasChangingLines = changingLines.length > 0;

  // 变卦计算：老阳(9)变阴，老阴(6)变阳
  // yinYangLines 顺序 = 下→上，与 getTrigramFromSums 一致
  const changedHexagram = useMemo(() => {
    if (!hasChangingLines || !hexagram) return null;
    const newLines = yinYangLines.map((l) => {
      if (l.total === 9) return 'yin';
      if (l.total === 6) return 'yang';
      return l.type === '阳' ? 'yang' : 'yin';
    });
    const newLowerSums = newLines.slice(0, 3).map((t) => t === 'yang' ? 7 : 8);
    const newUpperSums = newLines.slice(3, 6).map((t) => t === 'yang' ? 7 : 8);

    const newLower = getTrigramFromSums(newLowerSums);
    const newUpper = getTrigramFromSums(newUpperSums);
    return getHexagram(newUpper, newLower);
  }, [hasChangingLines, hexagram, yinYangLines]);

  useEffect(() => {
    if (!hexagram || !question) return;
    // 已有保存好的解读文本时，直接展示，不再调用大模型
    if (savedInterpretation) {
      setAiInterpretation(savedInterpretation);
      setAiLoading(false);
      return;
    }
    fetchAiInterpretation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hexagram, question]);

  const fetchAiInterpretation = useCallback(async () => {
    if (!hexagram) return;
    setAiLoading(true);
    setAiError('');
    setUseFallback(false);

    // 六爻详情（下→上的自然顺序）
    const linesDesc = yinYangLines
      .map((l) => `第${l.round}爻：${l.lineType}${l.changing ? '（变爻）' : ''}，爻辞：「${hexagram.lines[l.round - 1] || ''}」`)
      .join('\n');

    // 变卦信息
    let changingInfo = '';
    if (hasChangingLines && changedHexagram) {
      changingInfo = `\n\n本卦出现变爻：${changingLines.map((l) => `第${l.round}爻${l.lineType}（变${l.type === '阳' ? '阴' : '阳'}爻）`).join('、')}。\n变卦为第${changedHexagram.number}卦「${changedHexagram.name}」。\n变卦上卦：${changedHexagram.upperTrigram}（${TRIGRAM_MAP[changedHexagram.upperTrigram]?.nature || ''}）\n变卦下卦：${changedHexagram.lowerTrigram}（${TRIGRAM_MAP[changedHexagram.lowerTrigram]?.nature || ''}）\n变卦卦辞：「${changedHexagram.judgment}」`;
    }

    // 解卦规则
    const ruleText = getChangingLinesRule(
      changingLines.map((l) => ({ round: l.round, type: l.type })),
      hexagram,
      changedHexagram,
    );

    const prompt = `你是一位精通《周易》的易学大师，请严格遵循以下给定的解卦规则，对本次占卜做出详尽的解读。

【问卜之事】
${question}

【本卦信息】
第${hexagram.number}卦「${hexagram.name}」
上卦：${upperTrigram}（${upperInfo?.nature || ''}）
下卦：${lowerTrigram}（${lowerInfo?.nature || ''}）
卦辞：${hexagram.judgment}
象曰：${hexagram.image}
卦义：${hexagram.description}

【六爻详情】（从下到上排列，第1爻在底部，第6爻在顶部）
${linesDesc}${changingInfo}${ruleText}

请从以下角度进行详细解读（针对所问之事「${question}」给出具体分析，禁止泛泛而谈）：

### 卦象总体分析
此卦的卦德、卦义、上下卦相互作用所呈现的整体格局。

### 针对问题的具体解读
结合所问之事，从卦象给出具体的指引和建议。

### 按解卦规则解读
务必遵循上述【解卦规则】中指定的爻辞或卦辞进行解读，不可自行发挥。

### 时势与行动建议
当前时机如何？应当如何因应？

请用现代白话文（现代汉语）进行解读，通俗易懂，不要使用文言文。回答需具体、深入，字数不少于500字。

**重要输出规范（必须严格遵守）：**
1. 只能输出最终的卦象解读定稿文字，绝对不要输出英文、思考过程、推理步骤、分析框架、自我修正内容或提示词。
2. 全文必须使用中文，禁止使用任何英文单词或句子。
3. 不要出现 "因为...所以..." 这种教学式分析语气，请直接给出流畅自然的解读文章。
4. 不要列出 "1. 2. 3." 这类结构化编号，请用自然段落和适当的 ### 小标题组织内容。
5. 你的回答就是用户会直接阅读的最终文章，中间不能夹杂任何"我需要...""让我想想..."之类的内心独白。`;

    try {
      const resp = await callDeepseek(prompt);
      if (resp?.error) {
        throw new Error(resp.detail || resp.error);
      }

      const text = resp?.text || '';
      if (text) {
        setAiInterpretation(text);
        if (recordId) updateInterpretation(recordId, text);
      } else {
        throw new Error('返回内容为空');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '未知错误';
      setAiError(msg);
      const localText = generateLocalInterpretation();
      setAiInterpretation(localText);
      setUseFallback(true);
      if (recordId) updateInterpretation(recordId, localText);
    } finally {
      setAiLoading(false);
    }
  }, [hexagram, question, yinYangLines, changingLines, changedHexagram, upperTrigram, lowerTrigram, upperInfo, lowerInfo, recordId]);

  const generateLocalInterpretation = () => {
    if (!hexagram) return '';
    const linesDesc = yinYangLines
      .map((l) => `${l.lineType}${l.changing ? '（变）' : ''}`)
      .join('、');

    const ruleText = getChangingLinesRule(
      changingLines.map((l) => ({ round: l.round, type: l.type })),
      hexagram,
      changedHexagram,
    );

    const changingText = hasChangingLines
      ? `\n\n此卦出现变爻，主事态正在变化之中。${changingLines.map((l) => `第${l.round}爻为${l.lineType}，化为${l.type === '阳' ? '阴爻' : '阳爻'}`).join('；')}。变卦「${changedHexagram?.name || ''}」揭示了事情的走势与归宿。\n\n解卦要点：${ruleText.replace(/\n\n/g, '\n')}`
      : '';

    return `【卦象综述】

针对您所询问之「${question}」，此番问卜得第${hexagram.number}卦——「${hexagram.name}」。

${hexagram.name}，上卦${upperTrigram}为${upperInfo?.nature || ''}，下卦${lowerTrigram}为${lowerInfo?.nature || ''}，${hexagram.description}

【卦辞解析】

${hexagram.judgment}

此卦辞揭示：当前局势需要您${hexagram.name.includes('乾') ? '以刚健进取之心应对，时机成熟，当奋发而为，切勿踌躇' : hexagram.name.includes('坤') ? '以柔顺厚德之势处之，凡事不可强求，当以包容之心静待时机' : hexagram.name.includes('泰') ? '珍惜当下亨通之时，积极推进要事，大小皆宜，天地交泰' : hexagram.name.includes('否') ? '暂避锋芒，韬光养晦，勿急进取，静待局面转化' : hexagram.name.includes('震') ? '遇事当敬慎自省，振奋精神，化危为机' : '审时度势，顺应变化，保持内心的清明与笃定'}。

【六爻详析】

本卦六爻为：${linesDesc}。

${hasChangingLines
  ? `其中变爻出现于：${changingLines.map((l) => `第${l.round}爻（${l.lineType}）`).join('、')}。变爻代表事情正处于转折关键处，需特别关注。`
  : '六爻无变爻，卦象稳定，事态平稳发展。'}

象曰：${hexagram.image}此象提示您：当效法天地之道，顺势而行，则所求必有所获。${changingText}

【综合建议】

综观此卦，针对您所问之事——${hexagram.name.includes('吉') || hexagram.name.includes('泰') || hexagram.name.includes('益') ? '卦象总体利好，宜积极把握时机，勇于前进' : hexagram.name.includes('凶') || hexagram.name.includes('否') || hexagram.name.includes('困') ? '卦象提示当下有阻，宜谨慎行事，静观其变，守正待时' : '卦象中正平和，宜以诚信处事，循序渐进，不急不躁'}。

易曰：吉凶悔吝，皆由人定。占卜乃是天机的启示，最终命运仍握于自己手中，请以积极之心善用此卦的智慧指引。`;
  };

  // 从上到下展示（第6爻·最上 → 第1爻·最下）
  const linesTopToBottom = useMemo(() => [...yinYangLines].reverse(), [yinYangLines]);

  if (!hexagram) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        <div className="w-16 h-16 flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
        </div>
        <p className="text-foreground-500 text-sm mb-6">暂无卦象数据，请先完成打卦</p>
        <button
          onClick={() => navigate('/home')}
          className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
        >
          返回首页
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen pt-8 pb-6">
      <header className="w-full text-center mb-5 animate-fade-in">
        <h1 className="font-heading text-xl font-bold text-foreground-950 mb-1">卦象解读</h1>
        <p className="text-xs text-foreground-400">第{hexagram.number}卦</p>
      </header>

      {/* 卦象总览 */}
      <div className="w-full bg-background-100 rounded-xl border border-background-200 p-5 mb-4 animate-slide-up">
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 mb-4">
            <span className="text-lg font-heading font-bold text-primary-700">{hexagram.name}</span>
            <span className="text-xs text-primary-500">第{hexagram.number}卦</span>
          </div>

          {/* 卦象堆叠图 —— 从上到下：上爻→初爻 */}
          <div className="flex flex-col items-center gap-0 mb-4">
            {/* 上卦卦符 */}
            <div className="text-xs text-foreground-400 mb-0.5">上卦 {upperTrigram}</div>
            <div className="text-3xl mb-1">{upperInfo?.symbol}</div>

            {/* 六爻线条 —— 从上到下 */}
            <div className="flex flex-col items-center gap-[2px]">
              {linesTopToBottom.map((line, idx) => {
                const isYang = line.type === '阳';
                const visualIndex = 6 - line.round + 1; // 上爻=1, 初爻=6 for visual
                const isUpperGroup = line.round >= 4; // 第4-6爻=上卦
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-[10px] text-foreground-400 w-10 text-right">
                      第{line.round}爻
                    </span>
                    <div className="flex items-center gap-2">
                      {/* 爻线 */}
                      {isYang ? (
                        <div className="w-24 h-[5px] rounded-full bg-foreground-800"></div>
                      ) : (
                        <div className="w-24 h-[5px] flex justify-between">
                          <div className="w-10 h-full rounded-full bg-foreground-800"></div>
                          <div className="w-10 h-full rounded-full bg-foreground-800"></div>
                        </div>
                      )}
                      {/* 变爻标记 */}
                      {line.changing && (
                        <span className="text-[10px] text-primary-600 font-bold whitespace-nowrap bg-primary-50 px-1.5 py-0.5 rounded-full">
                          ○ 变
                        </span>
                      )}
                      {!line.changing && (
                        <span className="w-10"></span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 下卦卦符 */}
            <div className="text-3xl mt-1">{lowerInfo?.symbol}</div>
            <div className="text-xs text-foreground-400 mt-0.5">下卦 {lowerTrigram}</div>
          </div>
        </div>
      </div>

      {/* 变卦提示 */}
      {hasChangingLines && changedHexagram && (
        <div className="w-full bg-primary-50 rounded-xl border border-primary-200 p-4 mb-4 animate-slide-up">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1 h-4 rounded-full bg-primary-500"></div>
            <h3 className="font-heading text-sm font-semibold text-primary-800">发现变爻</h3>
          </div>
          <p className="text-xs text-primary-700 mb-2">
            本卦有{changingLines.length}个变爻（{changingLines.map((l) => `第${l.round}爻${l.lineType}`).join('、')}），变卦为「<strong>{changedHexagram.name}</strong>」。
          </p>
          <div className="flex items-center gap-3 mt-2">
            <div className="text-center">
              <span className="text-2xl block">{changedHexagram.lowerSymbol}</span>
              <span className="text-[10px] text-primary-500">下卦 {changedHexagram.lowerTrigram}</span>
            </div>
            <span className="text-primary-400 font-heading text-xl">→</span>
            <div className="text-center">
              <span className="text-2xl block">{changedHexagram.upperSymbol}</span>
              <span className="text-[10px] text-primary-500">上卦 {changedHexagram.upperTrigram}</span>
            </div>
          </div>
        </div>
      )}

      {/* 卦辞 */}
      <div className="w-full bg-background-100 rounded-xl border border-background-200 p-5 mb-4 animate-slide-up">
        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 flex items-center gap-2">
          <div className="w-1 h-4 rounded-full bg-primary-500"></div>
          卦辞
        </h3>
        <p className="text-sm text-foreground-700 leading-relaxed">{hexagram.judgment}</p>
      </div>

      <div className="w-full bg-background-100 rounded-xl border border-background-200 p-5 mb-4 animate-slide-up">
        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 flex items-center gap-2">
          <div className="w-1 h-4 rounded-full bg-accent-500"></div>
          象曰
        </h3>
        <p className="text-sm text-foreground-700 leading-relaxed">{hexagram.image}</p>
      </div>

      {/* 六爻爻辞全览 */}
      <div className="w-full bg-background-100 rounded-xl border border-background-200 p-5 mb-4 animate-slide-up">
        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-3 flex items-center gap-2">
          <div className="w-1 h-4 rounded-full bg-accent-500"></div>
          六爻爻辞
        </h3>
        <div className="space-y-2">
          {linesTopToBottom.map((line) => {
            const yaoCi = hexagram.lines[line.round - 1] || '';
            return (
              <div key={line.round} className={`flex items-start gap-3 p-2 rounded-lg ${
                line.changing ? 'bg-primary-50 border border-primary-200' : 'bg-background-50'
              }`}>
                <span className={`text-xs font-medium whitespace-nowrap mt-0.5 ${
                  line.type === '阳' ? 'text-accent-700' : 'text-secondary-700'
                }`}>
                  第{line.round}爻
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground-700 leading-relaxed">{yaoCi}</p>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                  line.changing
                    ? 'bg-primary-100 text-primary-700'
                    : line.type === '阳' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {line.changing ? '变爻' : line.type === '阳' ? '阳' : '阴'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* AI 解读 */}
      <div className="w-full bg-background-100 rounded-xl border border-background-200 p-5 mb-4 animate-slide-up">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base font-semibold text-foreground-900 flex items-center gap-2">
            <div className="w-1 h-4 rounded-full bg-secondary-500"></div>
            卦象详解
            {aiLoading && (
              <span className="text-xs text-foreground-400 font-normal ml-1 flex items-center gap-1">
                <span className="inline-block w-3 h-3 border border-foreground-300 border-t-transparent rounded-full animate-spin"></span>
                正在推演...
              </span>
            )}
          </h3>
        </div>

        {aiError && useFallback && (
          <div className="flex items-center gap-2 mb-3 p-3 rounded-lg bg-accent-50 border border-accent-200">
            <i className="ri-error-warning-line text-accent-600 text-sm"></i>
            <span className="text-xs text-accent-700 flex-1">AI 服务调用失败：{aiError}</span>
            <button
              onClick={() => fetchAiInterpretation()}
              className="text-xs text-accent-600 underline cursor-pointer whitespace-nowrap"
            >
              重试
            </button>
          </div>
        )}

        {aiLoading ? (
          <div className="space-y-2 animate-pulse">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-3 bg-background-200 rounded w-full"></div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-foreground-700 leading-relaxed">
            <MarkdownContent text={aiInterpretation} />
          </div>
        )}
      </div>

      {/* 免责声明 */}
      <div className="w-full bg-accent-50 rounded-xl border border-accent-200 p-4 mb-4 animate-slide-up">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-xl text-accent-600"></i>
          </div>
          <p className="text-xs text-accent-700 leading-relaxed">
            易经占卜旨在提供参考与启发，并非决定命运。每个人的命运掌握在自己手中，请理性看待占卜结果，保持积极乐观的心态。
          </p>
        </div>
      </div>

      <button
        onClick={() => navigate('/home')}
        className="w-full py-3 rounded-xl bg-primary-500 text-background-50 font-heading text-base font-bold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap active:scale-[0.98] mb-4"
      >
        重新问卜
      </button>
    </div>
  );
}
